import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { CourseCard, CourseCardSkeleton } from "@/components/dashboard/CourseCard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";

interface MyCourse {
  id: string;
  title: string;
  coverImageUrl: string | null;
  coverUrl: string | null;
  url: string | null;
  iconUrl: string | null;
  iconEmoji: string | null;
  iconName: string | null;
  iconColor: string | null;
  accentColor: string | null;
  nodeCount: number;
  completedNodes: number;
  totalXp: number;
  sortDate: string;
  isOwned: boolean;
}

export const MyCourses: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [courseToRename, setCourseToRename] = React.useState<MyCourse | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [courseToDelete, setCourseToDelete] = React.useState<MyCourse | null>(null);
  const queryKey = ["my-courses", user?.id] as const;

  const { data: courses, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const [ownedCoursesRes, progressRes, settingsRes] = await Promise.all([
        (supabase.from("courses") as any)
          .select(
            "id, title, cover_image_url, cover_url, icon_url, accent_color, node_count, created_at, url"
          )
          .eq("user_id", user!.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("user_course_progress")
          .select("course_id, completed_nodes, total_xp, last_active_at")
          .eq("user_id", user!.id),
        (supabase.from("user_course_settings") as any)
          .select("course_id, cover_image_url, icon_url, icon_emoji, icon_name, icon_color")
          .eq("user_id", user!.id),
      ]);

      if (ownedCoursesRes.error) throw ownedCoursesRes.error;
      if (progressRes.error) throw progressRes.error;
      if (settingsRes.error) throw settingsRes.error;

      const ownedCoursesData = ownedCoursesRes.data ?? [];
      const progressData = progressRes.data ?? [];
      const settingsData = settingsRes.data ?? [];

      const enrolledCourseIds = Array.from(
        new Set(progressData.map((progress) => progress.course_id))
      );

      const ownedCourseIds = new Set(ownedCoursesData.map((course) => course.id));
      const missingEnrolledIds = enrolledCourseIds.filter((id) => !ownedCourseIds.has(id));

      const enrolledCoursesRes = missingEnrolledIds.length
        ? await (supabase.from("courses") as any)
            .select(
              "id, title, cover_image_url, cover_url, icon_url, accent_color, node_count, created_at, url"
            )
            .in("id", missingEnrolledIds)
        : { data: [], error: null };

      if (enrolledCoursesRes.error) throw enrolledCoursesRes.error;

      const progressMap = new Map<string, { completedNodes: number; totalXp: number; lastActiveAt: string | null }>();
      for (const progress of progressData) {
        progressMap.set(progress.course_id, {
          completedNodes: progress.completed_nodes,
          totalXp: progress.total_xp,
          lastActiveAt: progress.last_active_at,
        });
      }

      const settingsMap = new Map<string, { cover_image_url: string | null; icon_url: string | null; icon_emoji: string | null; icon_name: string | null; icon_color: string | null }>();
      for (const setting of settingsData) {
        settingsMap.set(setting.course_id, {
          cover_image_url: setting.cover_image_url,
          icon_url: setting.icon_url,
          icon_emoji: setting.icon_emoji,
          icon_name: setting.icon_name ?? null,
          icon_color: setting.icon_color ?? null,
        });
      }

      const mergedCourses = new Map<string, MyCourse>();

      const addCourse = (course: any) => {
        const progress = progressMap.get(course.id);
        const overrides = settingsMap.get(course.id);

        mergedCourses.set(course.id, {
          id: course.id,
          title: course.title,
          coverImageUrl: overrides?.cover_image_url ?? course.cover_image_url ?? null,
          coverUrl: course.cover_url,
          url: course.url ?? null,
          iconUrl: overrides?.icon_url ?? course.icon_url ?? null,
          iconEmoji: overrides?.icon_emoji ?? null,
          iconName: overrides?.icon_name ?? null,
          iconColor: overrides?.icon_color ?? null,
          accentColor: course.accent_color,
          nodeCount: course.node_count,
          completedNodes: progress?.completedNodes ?? 0,
          totalXp: progress?.totalXp ?? 0,
          sortDate: progress?.lastActiveAt ?? course.created_at,
          isOwned: ownedCourseIds.has(course.id),
        });
      };

      ownedCoursesData.forEach(addCourse);
      (enrolledCoursesRes.data ?? []).forEach((course: any) => {
        if (!mergedCourses.has(course.id)) {
          addCourse(course);
        }
      });

      return Array.from(mergedCourses.values()).sort(
        (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
      );
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
  });

  React.useEffect(() => {
    if (!user?.id) return;

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey });
    };

    const channel = supabase
      .channel(`my-courses-${user.id}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "courses",
        filter: `user_id=eq.${user.id}`,
      }, invalidate)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "user_course_progress",
        filter: `user_id=eq.${user.id}`,
      }, invalidate)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "user_course_settings",
        filter: `user_id=eq.${user.id}`,
      }, invalidate)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, queryKey, user?.id]);

  const renameMutation = useMutation({
    mutationFn: async ({ courseId, title }: { courseId: string; title: string }) => {
      const { error } = await supabase
        .from("courses")
        .update({ title })
        .eq("id", courseId)
        .eq("user_id", user!.id);

      if (error) throw error;
    },
    onMutate: async ({ courseId, title }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousCourses = queryClient.getQueryData<MyCourse[]>(queryKey);

      queryClient.setQueryData<MyCourse[]>(queryKey, (current = []) =>
        current.map((course) =>
          course.id === courseId ? { ...course, title } : course,
        ),
      );

      return { previousCourses };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCourses) {
        queryClient.setQueryData(queryKey, context.previousCourses);
      }
      toast.error("Failed to rename course.");
    },
    onSuccess: () => {
      toast.success("Course renamed.");
      setCourseToRename(null);
      setRenameValue("");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (courseId: string) => {
      const { data, error } = await supabase.functions.invoke("delete-course", {
        body: { courseId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
    },
    onMutate: async (courseId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousCourses = queryClient.getQueryData<MyCourse[]>(queryKey);

      queryClient.setQueryData<MyCourse[]>(queryKey, (current = []) =>
        current.filter((course) => course.id !== courseId),
      );

      return { previousCourses };
    },
    onError: (_error, _courseId, context) => {
      if (context?.previousCourses) {
        queryClient.setQueryData(queryKey, context.previousCourses);
      }
      toast.error("Failed to delete course.");
    },
    onSuccess: () => {
      toast.success("Course deleted.");
      setCourseToDelete(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const openRenameDialog = (course: MyCourse) => {
    setCourseToRename(course);
    setRenameValue(course.title);
  };

  const submitRename = async () => {
    if (!courseToRename) return;

    const trimmedTitle = renameValue.trim();
    if (!trimmedTitle || trimmedTitle === courseToRename.title) {
      setCourseToRename(null);
      setRenameValue("");
      return;
    }

    await renameMutation.mutateAsync({
      courseId: courseToRename.id,
      title: trimmedTitle,
    });
  };

  if (!user || isLoading) {
    if (isLoading) {
      return (
        <section className="w-full mb-10">
          <div className="mb-4">
            <span className="text-base lg:text-lg font-semibold text-foreground">My Learning Courses</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <CourseCardSkeleton key={i} />
            ))}
          </div>
        </section>
      );
    }
    return null;
  }

  if (!courses?.length) {
    return null;
  }

  return (
    <section className="w-full mb-10">
      <div className="mb-4">
        <span className="text-base lg:text-lg font-semibold text-foreground">My Learning Courses</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {courses.map((course) => {
          const progressPct = course.nodeCount > 0
            ? Math.round((course.completedNodes / course.nodeCount) * 100)
            : 0;

          return (
            <CourseCard
              key={course.id}
              href={course.url ?? `/course/${course.id}`}
              title={course.title}
              coverImageUrl={course.coverImageUrl}
              coverUrl={course.coverUrl}
              iconUrl={course.iconUrl}
              iconEmoji={course.iconEmoji}
              iconName={course.iconName}
              iconColor={course.iconColor}
              accentColor={course.accentColor}
              showProgress
              progressPct={progressPct}
              actions={course.isOwned ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground"
                      aria-label={`Open actions for ${course.title}`}
                      onClick={(event) => event.preventDefault()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(event) => {
                        event.preventDefault();
                        openRenameDialog(course);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2 text-destructive focus:text-destructive"
                      onClick={(event) => {
                        event.preventDefault();
                        setCourseToDelete(course);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : undefined}
            />
          );
        })}
      </div>

      <Dialog
        open={!!courseToRename}
        onOpenChange={(open) => {
          if (!open) {
            setCourseToRename(null);
            setRenameValue("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename course</DialogTitle>
            <DialogDescription>Update the title shown across your dashboard and course pages.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              placeholder="Course title"
              maxLength={120}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void submitRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCourseToRename(null);
              setRenameValue("");
            }}>
              Cancel
            </Button>
            <Button onClick={() => void submitRename()} disabled={renameMutation.isPending || !renameValue.trim()}>
              {renameMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!courseToDelete} onOpenChange={(open) => !open && setCourseToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete course?</AlertDialogTitle>
            <AlertDialogDescription>
              {courseToDelete ? `"${courseToDelete.title}" will be permanently deleted for you and removed from the database.` : "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (courseToDelete) {
                  void deleteMutation.mutateAsync(courseToDelete.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
